import type { FormField } from '@lucid/lego';
import { getTitleOptions, getRegistrationProcessOptions } from '../../../../constants';
import type { BusinessSearchResult } from '../../../../../../api/services/lupp/order/dto.order';
import { isTruthyFlag } from '../../../../../../utils/tools';

type MailingAddressSelection = 'customer' | 'businessInformation' | 'Others';
type RegistrationAddressSelection = 'customer' | 'businessInformation' | 'Others';
type RegistrationAddressOption = {
  label: string;
  value: RegistrationAddressSelection;
  default: boolean;
};

const normalizeMailingAddressSelection = (value?: string | null): MailingAddressSelection => {
  if (!value) {
    return 'customer';
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === 'customer') {
    return 'customer';
  }

  if (normalized === 'businessinformation' || normalized === 'business') {
    return 'businessInformation';
  }

  if (normalized === 'others' || normalized === 'new') {
    return 'Others';
  }

  return 'businessInformation';
};

const isLucidRegistrationProcess = (value?: string | null): boolean => {
  if (!value) {
    return false;
  }

  const normalized = value.trim().toLowerCase();

  return normalized === 'lucid-registration' || normalized === 'lucid registration';
};

const normalizeRegistrationAddressSelection = (
  value?: string | null
): RegistrationAddressSelection => {
  if (!value) {
    return 'customer';
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === 'customeraddress' || normalized === 'customer') {
    return 'customer';
  }

  if (
    normalized === 'businessaddress' ||
    normalized === 'businessinformation' ||
    normalized === 'business'
  ) {
    return 'businessInformation';
  }

  if (normalized === 'newaddress' || normalized === 'others' || normalized === 'new') {
    return 'Others';
  }

  return 'businessInformation';
};

const buildRegistrationAddressOptions = (
  labels: Record<string, string> | undefined,
  selectedRegistrationAddressSelection: RegistrationAddressSelection,
  registrationType: MailingAddressSelection,
  hasRegistrationProcess = true
): RegistrationAddressOption[] => {
  const baseOptions: Array<{ label: string; value: RegistrationAddressSelection }> = [
    {
      label: labels?.['customer.sameAsCustomerAddress'] ?? 'Same as Customer Address',
      value: 'customer',
    },
    {
      label: labels?.['customer.sameAsBusinessAddress'] ?? 'Same as Business Address',
      value: 'businessInformation',
    },
    {
      label: labels?.['customer.addNewAddress'] ?? 'Add a New Address',
      value: 'Others',
    },
  ];

  const filteredOptions = baseOptions.filter((option) => {
    if (registrationType === 'customer') {
      return option.value !== 'businessInformation';
    }

    if (registrationType === 'businessInformation') {
      return option.value !== 'customer';
    }

    return true;
  });

  const selectedOption =
    filteredOptions.find((option) => option.value === selectedRegistrationAddressSelection)
      ?.value ?? filteredOptions[0]?.value;

  return filteredOptions.map((option) => ({
    ...option,
    default: hasRegistrationProcess && option.value === selectedOption,
  }));
};

/**
 * Creates form fields configuration for Individual Customer Details form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for customer fields
 * @returns Array of FormField configurations
 */
export const createCustomerFormFields = (
  labels?: Record<string, string>,
  defaultValues?: {
    firstName?: string | null;
    middleName?: string | null;
    lastName?: string | null;
    title?: string | null;
    dateOfBirth?: string | null;
    email?: string | null;
    phone?: string | null;
    address?: string | null;
    postalCode?: string | null;
    city?: string | null;
    country?: string | null;
    state?: string | null;
  },
  options?: {
    uploadedDocuments?: Array<{
      fileId: string;
      fileName: string;
    }>;
    onDocumentClick?: (fileId: string, fileName: string) => void;
    hasCompanySelected?: boolean;
  },
  locale?: string
): FormField[] => {
  return [
    {
      id: 'personalInfoGroup',
      fieldType: 'group',
      title: labels?.customerDetails ?? 'Customer Details',
      width: 'full',
      children: [
        {
          id: 'customerFirstName',
          fieldType: 'text',
          name: 'firstName',
          apiKey: 'customerFirstName',
          label: labels?.firstName ?? 'First Name',
          placeholder: labels?.firstNamePlaceholder ?? 'First Name',
          required: true,
          width: 'quarter',
          defaultValue: defaultValues?.firstName,
          validationMessages: {
            required: labels?.firstNameRequired ?? 'First name is required',
          },
        },
        {
          id: 'customerMiddleName',
          fieldType: 'text',
          name: 'middleName',
          apiKey: 'customerMiddleName',
          label: labels?.middleName ?? 'Middle Name',
          placeholder: labels?.middleNamePlaceholder ?? 'Enter Middle Name',
          required: false,
          width: 'quarter',
          defaultValue: defaultValues?.middleName,
        },
        {
          id: 'customerLastName',
          fieldType: 'text',
          name: 'lastName',
          apiKey: 'customerLastName',
          label: labels?.lastName ?? 'Last Name',
          placeholder: labels?.lastNamePlaceholder ?? 'Last Name',
          required: true,
          width: 'quarter',
          defaultValue: defaultValues?.lastName,
          validationMessages: {
            required: labels?.lastNameRequired ?? 'Last name is required',
          },
        },
        {
          id: 'customerTitle',
          fieldType: 'dropdown',
          name: 'title',
          apiKey: 'customerTitle',
          label: labels?.['form.title'] ?? 'Title',
          placeholder: labels?.['form.select'] ?? 'Select',
          required: true,
          width: 'quarter',
          options: getTitleOptions(labels, locale),
          defaultValue: defaultValues?.title,
          validationMessages: {
            required: labels?.['form.titleRequired'] ?? 'Title is required',
          },
        },
        {
          id: 'customerDateOfBirth',
          fieldType: 'date_picker',
          name: 'dateOfBirth',
          apiKey: 'customerDateOfBirth',
          label: labels?.dateOfBirth ?? 'Date of Birth',
          placeholder: labels?.datePlaceholder ?? 'Select Date',
          required: true,
          width: 'third',
          maxDate: 'today',
          minDate: new Date(1900, 0, 1),
          navigationMode: 'dropdown',
          defaultValue: defaultValues?.dateOfBirth,
          validationMessages: {
            required: labels?.dateOfBirthRequired ?? 'Date of birth is required',
          },
        },
        {
          id: 'customerEmail',
          fieldType: 'email',
          name: 'email',
          apiKey: 'customerEmail',
          label: labels?.email ?? 'Email',
          placeholder: labels?.emailPlaceholder ?? 'eliasvoss@gmail.com',
          required: false,
          disabled: true,
          width: 'third',
          defaultValue: defaultValues?.email,
        },
        {
          id: 'customerPhone',
          fieldType: 'phone',
          name: 'phone',
          apiKey: 'customerPhone',
          label: labels?.phone ?? 'Phone',
          required: false,
          disabled: true,
          width: 'third',
          selectedOption: 'DE',
          validationMessages: {
            required: labels?.['customer.phoneRequired'] ?? 'Phone is required',
            pattern: labels?.['customer.phoneInvalid'] ?? 'Please enter a valid phone number',
          },
          value: defaultValues?.phone,
        },
        {
          id: 'customerAddress',
          fieldType: 'text',
          name: 'address',
          apiKey: 'customerAddress',
          label: labels?.address ?? 'Address',
          placeholder: labels?.addressPlaceholder ?? 'Petuelring 130',
          required: true,
          width: 'full',
          defaultValue: defaultValues?.address,
          validationMessages: {
            required: labels?.addressRequired ?? 'Address is required',
            pattern: labels?.addressInvalid ?? 'Please enter a valid address',
          },
        },
        {
          id: 'zipCode',
          fieldType: 'text',
          name: 'zipCode',
          apiKey: 'zipCode',
          label: labels?.postalCode ?? 'Postal Code',
          placeholder: labels?.postalCodePlaceholder ?? 'Enter Postal Code',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.postalCode,
          postalCodeAutoPopulate: {
            cityField: 'city',
            countryField: 'country',
            regionField: 'stateProvince',
          },
          validationMessages: {
            required: labels?.postalCodeRequired ?? 'Postal code is required',
          },
        },
        {
          id: 'stateProvince',
          fieldType: 'text',
          name: 'stateProvince',
          apiKey: 'stateProvince',
          visible: false,
          disabled: options?.hasCompanySelected,
        },
        {
          id: 'city',
          fieldType: 'text',
          name: 'city',
          apiKey: 'city',
          label: labels?.city ?? 'City',
          placeholder: labels?.cityPlaceholder ?? 'Enter City',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.city,
          validationMessages: {
            required: labels?.cityRequired ?? 'City is required',
          },
        },
        {
          id: 'country',
          fieldType: 'text',
          name: 'country',
          apiKey: 'countryCode',
          label: labels?.['form.country'] ?? 'Country',
          placeholder: '',
          required: true,
          disabled: true,
          width: 'third',
          defaultValue: defaultValues?.country,
        },
        {
          id: 'documentsLabel',
          fieldType: 'content',
          content: labels?.documentsAvailable ?? 'Documents',
          variant: 'subtitle',
          size: 'extraSmall',
          color: 'white',
          width: 'full',
        },
        {
          id: 'driverLicense',
          fieldType: 'fileUpload',
          name: 'driverLicense',
          apiKey: 'driverLicense',
          variant: 'inline',
          label: labels?.driversLicense ?? "Driver's License",
          uploadButtonLabel: labels?.upload ?? 'Upload',
          helperText:
            labels?.driversLicenseHelper ??
            'JPG, PNG, GIF, PDF, DOC files supported (max 10 MB each)',
          accept: '.pdf,.jpg,.jpeg,.png,.gif,.doc,.docx',
          maxFileSize: 10485760,
          multiple: false,
          required: false,
          width: 'full',
          initialFiles:
            options?.uploadedDocuments && options.uploadedDocuments.length > 0
              ? options.uploadedDocuments.map(({ fileId, fileName }) => ({ fileId, fileName }))
              : undefined,
          onDocumentClick: options?.onDocumentClick,
          removable: true,
          validationMessages: {
            fileTooLarge:
              labels?.['upload.fileTooLarge'] ??
              'File size {{fileSize}}MB exceeds {{maxSize}}MB limit',
            invalidFileType:
              labels?.['upload.invalidFileType'] ?? 'File type {{fileType}} is not supported',
          },
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      width: 'full',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.save ?? 'Save',
          type: 'primary',
          size: 'large',
          color: 'neutral',
          theme: 'dark',
          isFluid: true,
        },
      ],
    },
  ];
};

/**
 * Resolves the mailing and registration address selections based on defaults, options,
 * and whether this is a Lucid registration process.
 */
const resolveAddressSelections = (
  isLucidRegistration: boolean,
  defaultValues?: {
    registrationType?: string | null;
    mailingAddressSelection?: string | null;
    registrationAddressSelection?: string | null;
    isNewMailingAddress?: boolean | string | null;
  },
  options?: {
    selectedMailingAddressSelection?: MailingAddressSelection;
    selectedRegistrationAddressSelection?: RegistrationAddressSelection | null;
  }
) => {
  const selectedMailingAddressSelection =
    options?.selectedMailingAddressSelection ??
    normalizeMailingAddressSelection(
      isLucidRegistration
        ? (defaultValues?.registrationType ??
            defaultValues?.mailingAddressSelection ??
            defaultValues?.registrationAddressSelection)
        : (defaultValues?.mailingAddressSelection ??
            defaultValues?.registrationType ??
            defaultValues?.registrationAddressSelection)
    );

  const effectiveMailingAddressSelection =
    isLucidRegistration && selectedMailingAddressSelection === 'Others'
      ? 'businessInformation'
      : selectedMailingAddressSelection;

  const registrationAddressSelectionFallback: RegistrationAddressSelection =
    effectiveMailingAddressSelection;

  const isNewMailingAddress = isTruthyFlag(defaultValues?.isNewMailingAddress);

  const selectedRegistrationAddressSelection = isNewMailingAddress
    ? 'Others'
    : normalizeRegistrationAddressSelection(
        options?.selectedRegistrationAddressSelection ??
          defaultValues?.registrationAddressSelection ??
          registrationAddressSelectionFallback
      );

  const effectiveRegistrationAddressSelection = isNewMailingAddress
    ? 'Others'
    : selectedRegistrationAddressSelection;

  return {
    selectedMailingAddressSelection,
    effectiveMailingAddressSelection,
    selectedRegistrationAddressSelection,
    effectiveRegistrationAddressSelection,
  };
};

/**
 * Determines the disabled dependency for register-to fields based on address selections.
 */
const resolveRegisterToDisabledDependency = (
  isLucidRegistration: boolean,
  effectiveRegistrationAddressSelection: RegistrationAddressSelection,
  effectiveMailingAddressSelection: MailingAddressSelection
): { name: string; value: string[] } | undefined => {
  if (isLucidRegistration && effectiveRegistrationAddressSelection !== 'Others') {
    return {
      name: 'registrationAddressSelection',
      value: ['customer', 'businessInformation'],
    };
  }
  if (!isLucidRegistration && effectiveMailingAddressSelection !== 'Others') {
    return {
      name: 'mailingAddressSelection',
      value: ['customer', 'businessInformation'],
    };
  }
  return undefined;
};

/**
 * Builds the company search default value from existing default values.
 */
const buildCompanySearchDefault = (defaultValues?: {
  companyName?: string | null;
  companyDuns?: string | null;
  companyAddress?: string | null;
  companyPostalCode?: string | null;
  companyCity?: string | null;
  companyCountry?: string | null;
  companyState?: string | null;
}): BusinessSearchResult | null => {
  if (!defaultValues?.companyName) return null;
  const displayText = [
    defaultValues.companyName,
    defaultValues.companyCity,
    defaultValues.companyCountry,
  ]
    .filter((value): value is string => Boolean(value?.trim()))
    .join(' - ');
  return {
    duns: defaultValues.companyDuns ?? '',
    name: defaultValues.companyName,
    primaryName: defaultValues.companyName,
    isOutOfBusiness: false,
    address: defaultValues.companyAddress ?? '',
    city: defaultValues.companyCity ?? '',
    postalCode: defaultValues.companyPostalCode ?? '',
    country: defaultValues.companyCountry ?? '',
    countryCode: '',
    region: defaultValues.companyState ?? undefined,
    displayText: displayText || defaultValues.companyName,
  };
};

/**
 * Maps customer form fields from the generic customer form to the business-specific field names.
 */
const mapCustomerDetailsFields = (
  customerFormFields: FormField[],
  customerFieldMapping: Record<
    string,
    {
      name: string;
      apiKey: string;
      disabled?: boolean;
      postalCodeAutoPopulate?: { cityField: string; countryField: string; regionField: string };
    }
  >
) => {
  const firstField = customerFormFields[0];
  if (
    !firstField ||
    firstField.fieldType !== 'group' ||
    !('children' in firstField) ||
    !firstField.children
  ) {
    return [];
  }
  return firstField.children.map((field) => {
    if (!('name' in field) || !('apiKey' in field)) return field;
    if (typeof field.id !== 'string') return field;
    const mappedField = customerFieldMapping[field.id];
    return mappedField ? { ...field, ...mappedField } : field;
  });
};

/**
 * Creates form fields configuration for Business Customer Details form
 *
 * @param labels - i18n labels for form field labels and placeholders
 * @param defaultValues - Default values for business customer fields
 * @param options - Optional runtime integrations (e.g., uploaded documents and company change callback).
 * Uploaded documents are intentionally optional because file upload handling is orchestrated outside
 * of form field configuration and passed in only when available.
 * @returns Array of FormField configurations
 */
export const createBusinessFormFields = (
  labels?: Record<string, string>,
  defaultValues?: {
    companyName?: string | null;
    companyDuns?: string | null;
    companyTaxId?: string | null;
    companyAddress?: string | null;
    companyPostalCode?: string | null;
    companyCity?: string | null;
    companyCountry?: string | null;
    companyState?: string | null;
    email?: string | null;
    phone?: string | null;
    registrationProcess?: string | null;
    registerToAddress?: string | null;
    registerToPostalCode?: string | null;
    registerToCity?: string | null;
    registerToCountry?: string | null;
    registerToState?: string | null;
    registrationType?: string | null;
    mailingAddressSelection?: string | null;
    registrationAddressSelection?: string | null;
    isNewMailingAddress?: boolean | string | null;
    deliverToFirstName?: string | null;
    deliverToMiddleName?: string | null;
    deliverToLastName?: string | null;
    deliverToTitle?: string | null;
    deliverToEmail?: string | null;
    deliverToLocation?: string | null;
    customerFirstName?: string | null;
    customerMiddleName?: string | null;
    customerLastName?: string | null;
    customerTitle?: string | null;
    customerDateOfBirth?: string | null;
    customerEmail?: string | null;
    customerPhone?: string | null;
    customerAddress?: string | null;
    customerPostalCode?: string | null;
    customerCity?: string | null;
    customerCountry?: string | null;
  },
  options?: {
    uploadedDocuments?: Array<{
      fileId: string;
      fileName: string;
    }>;
    onCompanyChange?: (company: BusinessSearchResult | null) => void;
    hasCompanySelected?: boolean;
    selectedMailingAddressSelection?: MailingAddressSelection;
    selectedRegistrationProcess?: string | null;
    selectedRegistrationAddressSelection?: RegistrationAddressSelection | null;
    userCountryCode?: string;
  },
  locale?: string
): FormField[] => {
  const selectedRegistrationProcess =
    options?.selectedRegistrationProcess ?? defaultValues?.registrationProcess;
  const isLucidRegistration = isLucidRegistrationProcess(selectedRegistrationProcess);
  const companySearchDefaultValue = buildCompanySearchDefault(defaultValues);

  const {
    effectiveMailingAddressSelection,
    selectedRegistrationAddressSelection,
    effectiveRegistrationAddressSelection,
  } = resolveAddressSelections(isLucidRegistration, defaultValues, options);

  const registrationAddressOptions = buildRegistrationAddressOptions(
    labels,
    selectedRegistrationAddressSelection,
    effectiveMailingAddressSelection,
    !!selectedRegistrationProcess
  );

  const registerToDisabledDependency = resolveRegisterToDisabledDependency(
    isLucidRegistration,
    effectiveRegistrationAddressSelection,
    effectiveMailingAddressSelection
  );

  const customerFormFields = createCustomerFormFields(
    labels,
    {
      firstName: defaultValues?.customerFirstName,
      middleName: defaultValues?.customerMiddleName,
      lastName: defaultValues?.customerLastName,
      title: defaultValues?.customerTitle,
      dateOfBirth: defaultValues?.customerDateOfBirth,
      email: defaultValues?.customerEmail,
      phone: defaultValues?.customerPhone,
      address: defaultValues?.customerAddress,
      postalCode: defaultValues?.customerPostalCode,
      city: defaultValues?.customerCity,
      country: defaultValues?.customerCountry,
      state: defaultValues?.registerToState ?? defaultValues?.companyState,
    },
    options,
    locale
  );

  const customerFieldMapping: Record<
    string,
    {
      name: string;
      apiKey: string;
      disabled?: boolean;
      postalCodeAutoPopulate?: {
        cityField: string;
        countryField: string;
        regionField: string;
      };
    }
  > = {
    customerFirstName: {
      name: 'customerFirstName',
      apiKey: 'customerFirstName',
    },
    customerMiddleName: {
      name: 'customerMiddleName',
      apiKey: 'customerMiddleName',
    },
    customerLastName: {
      name: 'customerLastName',
      apiKey: 'customerLastName',
    },
    customerTitle: {
      name: 'customerTitle',
      apiKey: 'customerTitle',
    },
    customerDateOfBirth: {
      name: 'customerDateOfBirth',
      apiKey: 'customerDateOfBirth',
    },
    customerEmail: {
      name: 'customerEmail',
      apiKey: 'customerEmail',
    },
    customerPhone: {
      name: 'customerPhone',
      apiKey: 'customerPhone',
    },
    customerAddress: {
      name: 'customerAddress',
      apiKey: 'customerAddress',
    },
    zipCode: {
      name: 'customerPostalCode',
      // Keep legacy apiKey for backward compatibility with existing API mapping.
      apiKey: 'zipCode',
      postalCodeAutoPopulate: {
        cityField: 'customerCity',
        countryField: 'customerCountry',
        regionField: 'customerStateProvince',
      },
    },
    stateProvince: {
      name: 'customerStateProvince',
      apiKey: 'customerStateProvince',
    },
    city: {
      name: 'customerCity',
      apiKey: 'customerCity',
    },
    country: {
      name: 'customerCountry',
      apiKey: 'customerCountry',
      disabled: true,
    },
  };

  const customerDetailsFields = mapCustomerDetailsFields(customerFormFields, customerFieldMapping);
  return [
    {
      id: 'customerDetailsGroup',
      fieldType: 'group',
      title: labels?.customerDetails ?? 'Customer Details',
      width: 'full',
      collapsible: true,
      defaultCollapsed: true,
      children: customerDetailsFields,
    },
    {
      id: 'businessInfoGroup',
      fieldType: 'group',
      title: labels?.businessDetails ?? 'Business Details',
      width: 'full',
      collapsible: true,
      defaultCollapsed: false,
      children: [
        {
          id: 'companyName',
          fieldType: 'custom',
          name: 'companyName',
          apiKey: 'companyName',
          component: 'BusinessNameSearch',
          required: true,
          width: 'half',
          defaultValue: companySearchDefaultValue,
          componentProps: {
            label: labels?.companyName ?? 'Company Name',
            placeholder: labels?.companyNamePlaceholder ?? 'Search for company...',
            required: true,
            disabled: true,
            dataTestId: 'company-name-search',
            defaultValue: companySearchDefaultValue,
            onChange: options?.onCompanyChange,
            messages: {
              searchingText: labels?.companyNameSearching ?? 'Searching...',
              apiErrorText:
                labels?.companyNameApiError ??
                'Search is temporarily unavailable. Please try again later.',
              minCharsText: labels?.companyNameMinChars ?? 'Type at least 3 characters to search',
              noResultsText: labels?.companyNameNoResults ?? 'No matching results found.',
              validationRequiredText:
                labels?.companyNameValidationRequired ??
                'Please select a valid Company from the list.',
            },
          },
          validationMessages: {
            required: labels?.companyNameRequired ?? 'Please select a valid Company from the list.',
          },
        },
        {
          id: 'companyDuns',
          fieldType: 'text',
          name: 'companyDuns',
          apiKey: 'companyDuns',
          visible: false,
          disabled: true,
          defaultValue: defaultValues?.companyDuns,
        },
        {
          id: 'companyTaxId',
          fieldType: 'text',
          name: 'companyTaxId',
          apiKey: 'companyTaxId',
          label: labels?.companyTaxId ?? 'Tax ID',
          placeholder: labels?.companyTaxIdPlaceholder ?? 'Enter tax ID',
          required: false,
          width: 'half',
          defaultValue: defaultValues?.companyTaxId,
        },
        {
          id: 'companyAddress',
          fieldType: 'text',
          name: 'companyAddress',
          apiKey: 'companyAddress',
          label: labels?.companyAddress ?? 'Company Address',
          placeholder: labels?.companyAddressPlaceholder ?? 'Enter company address',
          required: true,
          width: 'full',
          defaultValue: defaultValues?.companyAddress,
          disabled: true,
          validationMessages: {
            required: labels?.companyAddressRequired ?? 'Company address is required',
          },
        },
        {
          id: 'companyPostalCode',
          fieldType: 'text',
          name: 'companyPostalCode',
          apiKey: 'companyPostalCode',
          label: labels?.['form.postalCode'] ?? 'Postal Code',
          placeholder: labels?.['form.postalCodePlaceholder'] ?? 'Enter Postal Code',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.companyPostalCode,
          disabled: true,
          postalCodeAutoPopulate: {
            cityField: 'companyCity',
            countryField: 'companyCountry',
            regionField: 'companyStateProvince',
          },
          validationMessages: {
            required: labels?.postalCodeRequired ?? 'Postal code is required',
          },
        },
        {
          id: 'companyStateProvince',
          fieldType: 'text',
          name: 'companyStateProvince',
          apiKey: 'companyStateProvince',
          visible: false,
          disabled: true,
        },
        {
          id: 'companyCity',
          fieldType: 'text',
          name: 'companyCity',
          apiKey: 'companyCity',
          label: labels?.['form.city'] ?? 'City',
          placeholder: labels?.['form.cityPlaceholder'] ?? 'Enter City',
          required: true,
          width: 'third',
          defaultValue: defaultValues?.companyCity,
          disabled: true,
          validationMessages: {
            required: labels?.cityRequired ?? 'City is required',
          },
        },
        {
          id: 'companyCountry',
          fieldType: 'text',
          name: 'companyCountry',
          apiKey: 'companyCountry',
          label: labels?.['form.country'] ?? 'Country',
          placeholder: '',
          required: true,
          disabled: true,
          width: 'third',
          defaultValue: defaultValues?.companyCountry,
          validationMessages: {
            required: labels?.countryRequired ?? 'Country is required',
          },
        },
        {
          id: 'companyEmail',
          fieldType: 'email',
          name: 'companyEmail',
          apiKey: 'companyEmail',
          label: labels?.email ?? 'Email',
          placeholder: labels?.emailPlaceholder ?? 'company@example.com',
          required: false,
          disabled: true,
          width: 'half',
          defaultValue: defaultValues?.email,
        },
        {
          id: 'companyPhone',
          fieldType: 'phone',
          name: 'companyPhone',
          apiKey: 'companyPhone',
          label: labels?.phone ?? 'Phone',
          required: false,
          disabled: true,
          width: 'half',
          selectedOption: 'DE',
          validationMessages: {
            required: labels?.['customer.phoneRequired'] ?? 'Phone is required',
            pattern: labels?.['customer.phoneInvalid'] ?? 'Please enter a valid phone number',
          },
          value: defaultValues?.phone,
        },
      ],
    },
    {
      id: 'registerToGroup',
      fieldType: 'group',
      title: labels?.registerTo ?? 'Register To',
      width: 'full',
      collapsible: true,
      defaultCollapsed: true,
      children: [
        {
          id: 'registrationProcess',
          fieldType: 'dropdown',
          name: 'registrationProcess',
          apiKey: 'registrationProcess',
          label: labels?.registrationProcess ?? 'Registration Process',
          placeholder: labels?.registrationProcessPlaceholder ?? 'Select Process',
          required: true,
          width: 'full',
          options: getRegistrationProcessOptions(labels, options?.userCountryCode),
          defaultValue: defaultValues?.registrationProcess,
          validationMessages: {
            required: labels?.registrationProcessRequired ?? 'Registration process is required',
          },
        },
        {
          id: 'mailingAddressTitle',
          fieldType: 'content',
          content:
            (isLucidRegistration
              ? (labels?.registrationType ?? 'Registration Type')
              : labels?.mailingAddressSelection) ?? 'Mailing Address Selection',
          variant: 'label',
          size: 'small',
          color: 'gray-5',
          width: 'full',
          dependency: { name: 'registrationProcess' },
        },
        {
          id: 'mailingAddressSelection',
          fieldType: 'radio_group',
          name: 'mailingAddressSelection',
          apiKey: 'mailingAddressSelection',
          width: 'full',
          layout: 'horizontal',
          dependency: { name: 'registrationProcess' },
          defaultValue: selectedRegistrationProcess ? effectiveMailingAddressSelection : undefined,
          options: [
            {
              label:
                (isLucidRegistration
                  ? (labels?.customer ?? 'Customer')
                  : labels?.['customer.sameAsCustomerAddress']) ?? 'Same as Customer Address',
              value: 'customer',
              default:
                !!selectedRegistrationProcess && effectiveMailingAddressSelection === 'customer',
            },
            {
              label:
                (isLucidRegistration
                  ? (labels?.business ?? 'Business')
                  : labels?.['customer.sameAsBusinessAddress']) ?? 'Same as Business Address',
              value: 'businessInformation',
              default:
                !!selectedRegistrationProcess &&
                effectiveMailingAddressSelection === 'businessInformation',
            },
            ...(isLucidRegistration
              ? []
              : [
                  {
                    label: labels?.['customer.addNewAddress'] ?? 'Add a New Address',
                    value: 'Others',
                    default:
                      !!selectedRegistrationProcess &&
                      effectiveMailingAddressSelection === 'Others',
                  },
                ]),
          ],
        },
        ...(isLucidRegistration
          ? [
              {
                id: 'registrationAddressSelectionTitle',
                fieldType: 'content' as const,
                content: labels?.registrationAddressSelection ?? 'Registration Address Selection',
                variant: 'label' as const,
                size: 'small' as const,
                color: 'gray-5' as const,
                width: 'full' as const,
                dependency: { name: 'registrationProcess' } as const,
              },
              {
                id: 'registrationAddressSelection',
                fieldType: 'radio_group' as const,
                name: 'registrationAddressSelection',
                apiKey: 'registrationAddressSelection',
                width: 'full' as const,
                layout: 'horizontal' as const,
                options: registrationAddressOptions,
                defaultValue: selectedRegistrationProcess
                  ? effectiveRegistrationAddressSelection
                  : undefined,
                dependency: { name: 'registrationProcess' } as const,
              },
            ]
          : []),
        {
          id: 'registerToAddress',
          fieldType: 'text',
          name: 'registerToAddress',
          apiKey: 'registerToAddress',
          label: labels?.registerAddress ?? 'Address',
          placeholder: labels?.addressPlaceholder ?? 'Enter address',
          required: effectiveMailingAddressSelection === 'Others',
          width: 'full',
          defaultValue: defaultValues?.registerToAddress,
          dependency: {
            name: isLucidRegistration ? 'registrationAddressSelection' : 'mailingAddressSelection',
            value: ['customer', 'businessInformation', 'Others'],
          },
          disabledDependency: registerToDisabledDependency,
          validationMessages: {
            required: labels?.addressRequired ?? 'Address is required',
          },
        },
        {
          id: 'registerToZipCode',
          fieldType: 'text',
          name: 'registerToZipCode',
          apiKey: 'registerToZipCode',
          label: labels?.['form.postalCode'] ?? 'Postal Code',
          placeholder: labels?.['form.postalCodePlaceholder'] ?? 'Enter Postal Code',
          required: effectiveMailingAddressSelection === 'Others',
          width: 'third',
          defaultValue: defaultValues?.registerToPostalCode,
          postalCodeAutoPopulate: {
            cityField: 'registerToCity',
            countryField: 'registerToCountry',
            regionField: 'registerToStateProvince',
          },
          validationMessages: {
            required: labels?.postalCodeRequired ?? 'Postal code is required',
          },
          dependency: {
            name: isLucidRegistration ? 'registrationAddressSelection' : 'mailingAddressSelection',
            value: ['customer', 'businessInformation', 'Others'],
          },
          disabledDependency: registerToDisabledDependency,
        },
        {
          id: 'registerToStateProvince',
          fieldType: 'text',
          name: 'registerToStateProvince',
          apiKey: 'registerToStateProvince',
          visible: false,
          dependency: {
            name: 'mailingAddressSelection',
            value: ['customer', 'businessInformation', 'Others'],
          },
        },
        {
          id: 'registerToCity',
          fieldType: 'text',
          name: 'registerToCity',
          apiKey: 'registerToCity',
          label: labels?.['form.city'] ?? 'City',
          placeholder: labels?.['form.cityPlaceholder'] ?? 'Enter City',
          required: effectiveMailingAddressSelection === 'Others',
          width: 'third',
          defaultValue: defaultValues?.registerToCity,
          validationMessages: {
            required: labels?.cityRequired ?? 'City is required',
          },
          dependency: {
            name: isLucidRegistration ? 'registrationAddressSelection' : 'mailingAddressSelection',
            value: ['customer', 'businessInformation', 'Others'],
          },
          disabledDependency: registerToDisabledDependency,
        },
        {
          id: 'registerToCountry',
          fieldType: 'text',
          name: 'registerToCountry',
          apiKey: 'registerToCountry',
          label: labels?.['form.country'] ?? 'Country',
          placeholder: '',
          required: false,
          disabled: true,
          width: 'third',
          defaultValue: defaultValues?.registerToCountry,
          dependency: {
            name: isLucidRegistration ? 'registrationAddressSelection' : 'mailingAddressSelection',
            value: ['customer', 'businessInformation', 'Others'],
          },
          disabledDependency: registerToDisabledDependency,
        },
      ],
    },
    {
      id: 'deliverToGroup',
      fieldType: 'group',
      title: labels?.deliverTo ?? 'Deliver To',
      width: 'full',
      collapsible: true,
      defaultCollapsed: true,
      children: [
        {
          id: 'deliverToFirstName',
          fieldType: 'text',
          name: 'deliverToFirstName',
          apiKey: 'deliverToFirstName',
          label: labels?.firstName ?? 'First Name',
          placeholder: labels?.firstNamePlaceholder ?? 'Enter First Name',
          required: true,
          width: 'quarter',
          defaultValue: defaultValues?.deliverToFirstName,
          validationMessages: {
            required: labels?.firstNameRequired ?? 'First name is required',
          },
        },
        {
          id: 'deliverToMiddleName',
          fieldType: 'text',
          name: 'deliverToMiddleName',
          apiKey: 'deliverToMiddleName',
          label: labels?.middleName ?? 'Middle Name',
          placeholder: labels?.middleNamePlaceholder ?? 'Enter Middle Name',
          required: false,
          width: 'quarter',
          defaultValue: defaultValues?.deliverToMiddleName,
        },
        {
          id: 'deliverToLastName',
          fieldType: 'text',
          name: 'deliverToLastName',
          apiKey: 'deliverToLastName',
          label: labels?.lastName ?? 'Last Name',
          placeholder: labels?.lastNamePlaceholder ?? 'Enter Last Name',
          required: true,
          width: 'quarter',
          defaultValue: defaultValues?.deliverToLastName,
          validationMessages: {
            required: labels?.lastNameRequired ?? 'Last name is required',
          },
        },
        {
          id: 'deliverToTitle',
          fieldType: 'dropdown',
          name: 'deliverToTitle',
          apiKey: 'deliverToTitle',
          label: labels?.['form.title'] ?? 'Title',
          placeholder: labels?.['form.select'] ?? 'Select',
          required: true,
          width: 'quarter',
          options: getTitleOptions(labels, locale),
          defaultValue: defaultValues?.deliverToTitle,
        },
        {
          id: 'deliverToEmail',
          fieldType: 'email',
          name: 'deliverToEmail',
          apiKey: 'deliverToEmail',
          label: labels?.email ?? 'Email',
          placeholder: labels?.emailPlaceholder ?? 'Enter Email',
          required: true,
          width: 'full',
          defaultValue: defaultValues?.deliverToEmail,
          validationMessages: {
            required: labels?.emailRequired ?? 'Email is required',
            pattern: labels?.emailInvalid ?? 'Please enter a valid email address',
          },
        },
      ],
    },
    {
      id: 'formButtons',
      fieldType: 'buttonGroup',
      width: 'full',
      buttons: [
        {
          id: 'submitButton',
          label: labels?.save ?? 'Save',
          type: 'primary',
          size: 'large',
          color: 'neutral',
          theme: 'dark',
          isFluid: true,
        },
      ],
    },
  ];
};
